import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

const MOODS = {
  idle: { brow: 0, mouth: "M46 78 Q60 86 74 78", tilt: 0, lidScale: 1 },
  watching: { brow: -4, mouth: "M50 80 Q60 84 70 80", tilt: 8, lidScale: 1 },
  picked: { brow: -6, mouth: "M44 76 Q60 94 76 76", tilt: -4, lidScale: 1 },
  done: { brow: -3, mouth: "M44 76 Q60 94 76 76", tilt: 0, lidScale: 0.12 },
};

// Petit personnage à lunettes qui suit le curseur des yeux et réagit à la réservation.
const Watcher = ({ mood = "idle", className = "" }) => {
  const ref = useRef(null);
  const px = useMotionValue(0.6);
  const py = useMotionValue(0.3);
  const x = useSpring(px, { stiffness: 180, damping: 18 });
  const y = useSpring(py, { stiffness: 180, damping: 18 });
  const m = MOODS[mood] || MOODS.idle;
  const ox = useSpringOffset(x, 5);
  const oy = useSpringOffset(y, 3.5);

  useEffect(() => {
    const onMove = (e) => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height * 0.45;
      px.set(Math.max(-1, Math.min(1, (e.clientX - cx) / 260)));
      py.set(Math.max(-1, Math.min(1, (e.clientY - cy) / 200)));
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [px, py]);

  const pupil = (cx, cy) => <motion.circle r={4.2} fill="#08080a" style={{ x: ox, y: oy }} cx={cx} cy={cy} />;

  return (
    <motion.svg
      ref={ref}
      viewBox="0 0 120 130"
      className={className}
      animate={{ rotate: m.tilt, y: mood === "picked" ? [0, -8, 0] : [0, -3, 0] }}
      transition={{
        rotate: { type: "spring", stiffness: 120, damping: 12 },
        y: { duration: mood === "picked" ? 0.6 : 3, repeat: Infinity, ease: "easeInOut" },
      }}
      aria-hidden="true"
      data-testid={`booking-watcher-${mood}`}
    >
      <defs>
        <linearGradient id="watcher-body" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#6366f1" />
        </linearGradient>
      </defs>
      {/* corps */}
      <path d="M22 130 C22 60 36 28 60 28 C84 28 98 60 98 130 Z" fill="url(#watcher-body)" />
      {/* reflet */}
      <path d="M34 120 C34 70 42 48 54 40" stroke="rgba(255,255,255,0.25)" strokeWidth="4" strokeLinecap="round" fill="none" />
      {/* lunettes */}
      <g stroke="rgba(255,255,255,0.85)" strokeWidth="2.5" fill="rgba(8,8,10,0.25)">
        <rect x="30" y="52" width="24" height="20" rx="7" />
        <rect x="66" y="52" width="24" height="20" rx="7" />
        <path d="M54 61 L66 61" fill="none" />
      </g>
      {/* yeux + paupières (clignement) */}
      <motion.g
        style={{ originX: "60px", originY: "62px" }}
        animate={{ scaleY: mood === "done" ? m.lidScale : [1, 1, 0.08, 1] }}
        transition={mood === "done" ? { duration: 0.2 } : { duration: 4.2, repeat: Infinity, times: [0, 0.92, 0.96, 1] }}
      >
        <circle cx="42" cy="62" r="7" fill="#fff" />
        <circle cx="78" cy="62" r="7" fill="#fff" />
        {pupil(42, 62)}
        {pupil(78, 62)}
      </motion.g>
      {/* sourcils */}
      <motion.g stroke="#08080a" strokeWidth="2.5" strokeLinecap="round" fill="none" animate={{ y: m.brow }} transition={{ type: "spring", stiffness: 200, damping: 14 }}>
        <path d={`M33 44 Q42 ${mood === "watching" ? 38 : 41} 51 44`} />
        <path d={`M69 44 Q78 ${mood === "watching" ? 38 : 41} 87 44`} />
      </motion.g>
      {/* bouche */}
      <motion.path d={m.mouth} stroke="#08080a" strokeWidth="2.5" strokeLinecap="round" fill="none" animate={{ d: m.mouth }} transition={{ duration: 0.3 }} />
      {/* joues */}
      {mood !== "idle" && (
        <>
          <circle cx="32" cy="76" r="3.5" fill="rgba(255,120,150,0.45)" />
          <circle cx="88" cy="76" r="3.5" fill="rgba(255,120,150,0.45)" />
        </>
      )}
    </motion.svg>
  );
};

const useSpringOffset = (mv, amp) => {
  const out = useMotionValue(0);
  useEffect(() => mv.on("change", (v) => out.set(v * amp)), [mv, amp, out]);
  return out;
};

export default Watcher;
